import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface ModerationDailyRow {
  report_date: string
  reports_created: number
}

export interface AdminModerationStatsResult {
  dailyRows: ModerationDailyRow[]
  avgResolutionHours: number | null
  fromDate: string
  toDate: string
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

export function useAdminModerationStats(fromDate: Date, toDate: Date) {
  const fromStr = toISODate(fromDate)
  const toStr = toISODate(toDate)

  return useQuery({
    queryKey: ['admin-moderation-stats', fromStr, toStr],
    queryFn: async (): Promise<AdminModerationStatsResult> => {
      const [dailyRes, resolutionRes] = await Promise.all([
        supabase.rpc('get_admin_moderation_daily', {
          p_from_date: fromStr,
          p_to_date: toStr,
        }),
        supabase.rpc('get_admin_moderation_avg_resolution', {
          p_from_date: fromStr,
          p_to_date: toStr,
        }),
      ])
      if (dailyRes.error) throw dailyRes.error
      if (resolutionRes.error) throw resolutionRes.error

      const dailyRows = (Array.isArray(dailyRes.data) ? dailyRes.data : []) as Array<{
        report_date: string
        reports_created: number | string
      }>
      const normalizedDaily: ModerationDailyRow[] = dailyRows.map((r) => ({
        report_date: r.report_date,
        reports_created: Number(r.reports_created ?? 0),
      }))

      const resolutionRow = Array.isArray(resolutionRes.data) ? resolutionRes.data[0] : resolutionRes.data
      const avgResolutionHours =
        resolutionRow != null && resolutionRow.avg_resolution_hours != null
          ? Number(resolutionRow.avg_resolution_hours)
          : null

      return {
        dailyRows: normalizedDaily,
        avgResolutionHours,
        fromDate: fromStr,
        toDate: toStr,
      }
    },
    staleTime: 60_000,
  })
}
